import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import type {
  HeaderSection,
  NavItem,
  NavSubmenu,
} from "@appcaire/shared/public-navigation";

export type PublicSiteHeaderProps = {
  labels: {
    navigation: string;
    mobileNavigation: string;
    menu: string;
    openMenu: string;
    closeMenu: string;
    showSection: string;
  };
  homeHref: string;
  sections: HeaderSection[];
  login: NavItem;
  brand: ReactNode;
  renderHome: (
    href: string,
    brand: ReactNode,
    onClick?: () => void,
  ) => ReactNode;
  localeSwitcher?: ReactNode;
  renderLink: (
    item: NavItem,
    className: string,
    onClick?: () => void,
  ) => ReactNode;
  renderCta: (mobile: boolean, onClick?: () => void) => ReactNode;
};

const linkClass =
  "block rounded-lg px-3 py-2.5 text-sm text-[var(--cb-ink)] transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cb-action)] motion-reduce:transition-none";
const focusSelector =
  'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

function Submenu({
  submenu,
  renderLink,
  close,
  desktopMega = false,
}: {
  submenu: NavSubmenu;
  renderLink: PublicSiteHeaderProps["renderLink"];
  close: () => void;
  desktopMega?: boolean;
}) {
  return (
    <div
      className={
        desktopMega
          ? `grid gap-x-6 ${(submenu.groups?.length ?? 0) >= 3 ? "grid-cols-4" : "grid-cols-2"}`
          : undefined
      }
    >
      <div className={desktopMega ? "col-span-full" : undefined}>
        {renderLink(submenu.overview, linkClass, close)}
      </div>
      {submenu.items.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-[var(--cb-border)] pt-2">
          {submenu.items.map((item) => (
            <li key={item.href}>
              {renderLink(item, linkClass, close)}
              {item.items?.map((child) => (
                <div key={child.href}>
                  {renderLink(child, `${linkClass} ml-3`, close)}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
      {submenu.groups?.map((group) => (
        <div
          key={group.href ?? group.label}
          className={`mt-2 border-t border-[var(--cb-border)] pt-2 ${desktopMega && (group.items?.length ?? 0) > 8 ? "col-span-2" : ""}`}
        >
          {group.href ? (
            renderLink(
              { label: group.label, href: group.href },
              linkClass,
              close,
            )
          ) : (
            <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.06em] text-[var(--cb-slate)]">
              {group.label}
            </p>
          )}
          <div
            className={
              desktopMega && (group.items?.length ?? 0) > 8
                ? "grid grid-cols-2 gap-x-3"
                : undefined
            }
          >
            {group.items?.map((item) => (
              <div key={item.href}>
                {renderLink(
                  item,
                  group.href ? `${linkClass} ml-3` : linkClass,
                  close,
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Brand public navigation, shared by the website and dashboard auth shells. */
/** A section without sub-pages is a plain link: no toggle, no empty menu. */
function hasMenu(section: { submenu: NavSubmenu }) {
  return (
    section.submenu.items.length > 0 ||
    (section.submenu.groups?.length ?? 0) > 0
  );
}

export function PublicSiteHeader({
  labels,
  homeHref,
  sections,
  login,
  brand,
  renderHome,
  localeSwitcher,
  renderLink,
  renderCta,
}: PublicSiteHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const header = useRef<HTMLElement>(null);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (!mobileOpen || !dialog.current) return;
    const node = dialog.current;
    const openerNode = opener.current;
    const oldOverflow = document.body.style.overflow;
    const focusables = () =>
      Array.from(node.querySelectorAll<HTMLElement>(focusSelector)).filter(
        (x) => !x.closest("[hidden], [inert]"),
      );
    const restore = new Map<HTMLElement, string | null>();
    let current: HTMLElement = node;
    while (current.parentElement) {
      for (const sibling of Array.from(current.parentElement.children))
        if (sibling !== current && sibling instanceof HTMLElement) {
          if (!restore.has(sibling))
            restore.set(sibling, sibling.getAttribute("inert"));
          sibling.setAttribute("inert", "");
        }
      if (current.parentElement === document.body) break;
      current = current.parentElement;
    }
    const contain = (event: FocusEvent) => {
      if (!node.contains(event.target as Node)) focusables()[0]?.focus();
    };
    document.addEventListener("focusin", contain);
    document.body.style.overflow = "hidden";
    focusables()[0]?.focus();
    const media = window.matchMedia("(min-width: 1024px)");
    const resize = () => {
      if (media.matches) closeMobile();
    };
    media.addEventListener("change", resize);
    return () => {
      document.removeEventListener("focusin", contain);
      media.removeEventListener("change", resize);
      restore.forEach((value, el) =>
        value === null
          ? el.removeAttribute("inert")
          : el.setAttribute("inert", value),
      );
      document.body.style.overflow = oldOverflow;
      openerNode?.focus();
    };
  }, [mobileOpen, closeMobile]);

  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMobile();
      return;
    }
    if (event.key !== "Tab") return;
    const nodes = Array.from(
      dialog.current?.querySelectorAll<HTMLElement>(focusSelector) ?? [],
    ).filter((x) => !x.closest("[hidden], [inert]"));
    if (event.shiftKey && document.activeElement === nodes[0]) {
      event.preventDefault();
      nodes.at(-1)?.focus();
    } else if (!event.shiftKey && document.activeElement === nodes.at(-1)) {
      event.preventDefault();
      nodes[0]?.focus();
    }
  };

  useEffect(() => {
    if (!active) return;
    const outside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) setActive(null);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [active]);

  const toggleDesktop = (id: string) => {
    setActive((value) => (value === id ? null : id));
  };
  return (
    <header
      ref={header}
      className="fixed inset-x-0 top-0 z-50 border-b border-caire-hairline bg-caire-page/95 backdrop-blur"
    >
      <nav
        className="flex h-16 w-full items-center justify-between gap-4 px-4 xl:px-6"
        aria-label={labels.navigation}
      >
        {renderHome(homeHref, brand)}
        <div className="ml-2 hidden flex-1 items-center gap-2 lg:flex xl:ml-4 xl:gap-5">
          {sections.map((section) => (
            <div
              key={section.id}
              role="presentation"
              className={
                section.submenu.layout === "mega" ? "static" : "relative"
              }
              onBlur={(event) => {
                if (
                  event.relatedTarget &&
                  !event.currentTarget.contains(event.relatedTarget)
                )
                  setActive(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape" && active === section.id) {
                  setActive(null);
                  (
                    event.currentTarget.querySelector(
                      "button",
                    ) as HTMLButtonElement | null
                  )?.focus();
                }
              }}
            >
              <div className="flex items-center">
                {renderLink(
                  { label: section.label, href: section.href },
                  "whitespace-nowrap rounded-sm py-2 font-medium text-[var(--cb-slate)] hover:text-[var(--cb-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--cb-action)]",
                )}
                {hasMenu(section) && (
                  <button
                    type="button"
                    aria-expanded={active === section.id}
                    aria-controls={`${section.id}-menu`}
                    aria-label={`${labels.showSection} ${section.label}`}
                    className="ml-0.5 flex h-8 w-6 items-center justify-center rounded-md text-[var(--cb-slate)] hover:bg-white xl:ml-1 xl:w-8"
                    onClick={() => toggleDesktop(section.id)}
                  >
                    <ChevronDown
                      className={`h-4 w-4 ${active === section.id ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </div>
              {hasMenu(section) && active === section.id && (
                <div
                  id={`${section.id}-menu`}
                  className={`absolute top-full z-50 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-xl border border-[var(--cb-border)] bg-[var(--cb-page)] p-3 shadow-xl ${section.submenu.layout === "mega" ? "inset-x-0 mx-auto w-[min(64rem,calc(100vw-2rem))] p-5" : "left-0 w-80"}`}
                >
                  <Submenu
                    submenu={section.submenu}
                    renderLink={renderLink}
                    close={() => setActive(null)}
                    desktopMega={section.submenu.layout === "mega"}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden shrink-0 items-center gap-1 lg:flex xl:gap-2">
          {localeSwitcher}
          {renderLink(
            login,
            "whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-[var(--cb-slate)] hover:text-[var(--cb-ink)]",
          )}
          {renderCta(false)}
        </div>
        <button
          ref={opener}
          type="button"
          aria-expanded={mobileOpen}
          aria-controls="public-site-mobile-menu"
          aria-label={labels.openMenu}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-[var(--cb-border-strong)] bg-white text-[var(--cb-ink)] lg:hidden"
          onClick={() => {
            setActive(null);
            setMobileOpen(true);
          }}
        >
          <Menu className="h-6 w-6" />
        </button>
      </nav>
      {mobileOpen && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the modal menu's focus trap and Escape close listen on the dialog container (WAI-ARIA dialog pattern).
        <div
          ref={dialog}
          id="public-site-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label={labels.menu}
          onKeyDown={keyDown}
          className="fixed inset-0 z-[60] h-[100dvh] overflow-y-auto overscroll-contain bg-[var(--cb-page)] lg:hidden"
        >
          <div className="flex h-16 items-center justify-between border-b border-[var(--cb-border)] px-4">
            {renderHome(homeHref, brand, closeMobile)}
            <button
              type="button"
              aria-label={labels.closeMenu}
              onClick={closeMobile}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-[var(--cb-border-strong)] bg-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="mx-auto max-w-xl p-4 pb-10">
            <nav className="space-y-2" aria-label={labels.mobileNavigation}>
              {sections.map((section) => (
                <div key={section.id}>
                  <div className="flex items-center rounded-lg hover:bg-white">
                    {renderLink(
                      { label: section.label, href: section.href },
                      "flex-1 rounded-lg px-4 py-3 text-lg font-medium text-[var(--cb-ink)]",
                      closeMobile,
                    )}
                    {hasMenu(section) && (
                      <button
                        type="button"
                        aria-expanded={mobileExpanded === section.id}
                        aria-controls={`${section.id}-mobile-menu`}
                        aria-label={`${labels.showSection} ${section.label}`}
                        className="flex h-12 w-12 items-center justify-center rounded-lg"
                        onClick={() =>
                          setMobileExpanded((v) =>
                            v === section.id ? null : section.id,
                          )
                        }
                      >
                        <ChevronDown
                          className={`h-5 w-5 ${mobileExpanded === section.id ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                  {hasMenu(section) && mobileExpanded === section.id && (
                    <div
                      id={`${section.id}-mobile-menu`}
                      className="ml-4 border-l-2 border-[var(--cb-border)] pb-3 pl-3"
                    >
                      <Submenu
                        submenu={section.submenu}
                        renderLink={renderLink}
                        close={closeMobile}
                      />
                    </div>
                  )}
                </div>
              ))}
            </nav>
            <div className="mt-6 space-y-4 border-t border-[var(--cb-border)] pt-6">
              {renderCta(true, closeMobile)}
              {renderLink(
                login,
                "block text-center rounded-lg px-3 py-2.5 text-sm font-semibold text-[var(--cb-slate)]",
                closeMobile,
              )}
              <div className="flex justify-center">{localeSwitcher}</div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
